import React, { useState } from 'react'
import Modal, { ModalProps } from 'components/modal'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { useApplicationStore } from 'hooks/application'
import Button from 'components/button'
import { Csv } from 'components/icons'
import api from 'helpers/api'
import Subscription from 'components/subscription'
import { useAuth } from 'hooks/auth'
import { toUTCString } from 'components/spreadsheet/helpers/format'
import { BACKUP_ALLOWED_DAYS, ORG_PLAN_TIERS } from 'app-constants'

const BackupModal: React.FC<ModalProps> = ({ id, open, setOpen }) => {
  const { spreadsheetData } = useDataContext()
  const { setSnackbarMessage, displayErrorMessage } = useApplicationStore()
  const { user } = useAuth()

  const [selectedDate, setSelectedDate] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const requestCsvBackup = () => {
    setIsLoading(true)

    api
      .requestCsvBackup(spreadsheetData.tableDetails.publicId, toUTCString(selectedDate))
      .then(() => {
        setSnackbarMessage({
          status: 'success',
          message: 'CSV backup is being generated and will be emailed to you shortly'
        })
        setIsLoading(false)
        setOpen(false)
      })
      .catch((error) => {
        displayErrorMessage(error)
        setIsLoading(false)
      })
  }

  const allowedDays = BACKUP_ALLOWED_DAYS[user?.team?.orgPlanTier || ORG_PLAN_TIERS.PROFESSIONAL]

  return (
    <Modal id={id} open={open} setOpen={setOpen} title="Table Backups">
      <div className="flex flex-col">
        <Subscription type="team" message="access backups older than 30 days" />
        <div className="mb-8">
          Select a date and a CSV backup of the table at that point in time will be sent to your email. This will
          include all user changes to rows and columns but will not include changes to formulas, joins or scripts. To
          restore a backup, delete your row data and then import the CSV file using the import button.
        </div>
        <input
          className="mb-8"
          type="datetime-local"
          min={
            new Date(new Date().getTime() - allowedDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0] + 'T00:00'
          }
          onChange={(event) => setSelectedDate(event.target.value)}
        />
        <Button
          className="ml-auto"
          internalType="accept"
          isLoading={isLoading}
          onClick={() => requestCsvBackup()}
          disabled={selectedDate === ''}
        >
          <Csv />
          <span className="ml-[7px]">Generate CSV Backup</span>
        </Button>
      </div>
    </Modal>
  )
}

export default BackupModal
